import { FormField, Input } from '../../../components/ui/FormField.js';
import { LastChanged } from '../parts/LastChanged.js';
import {
  MODE_CHIP,
  ModeControl,
  PendingChanges,
  SettingCard,
  StatusChip,
  modeTone,
} from '../parts/parts.js';
import type { CardProps } from './cardProps.js';

const UNPAID_HELP = {
  off: 'Off: nothing is cancelled on its own.',
  count:
    'Count only: changes nothing; lists in the server log each booking it would cancel, to check before On.',
  on: 'On: cancels each booking still unpaid after the minutes below, and tells the customer.',
} as const;

const WORKFLOW_HELP = {
  off: 'Off: nothing moves on its own. Staff move each booking.',
  count:
    'Count only: changes nothing; lists in the server log each booking it would move, to check before On.',
  on: 'On: a paid booking is checked out 15 minutes before pickup and completed when the rental ends. The customer is not sent a message about either.',
} as const;

/** 324 (#678) + 356: the payment hold and the unpaid auto-cancel. */
export function UnpaidBookingsCard({ draft, latest, refusal }: CardProps) {
  const saved = draft.saved?.booking_timers;
  const timers = draft.value('booking_timers');
  if (!saved || !timers) return null;
  const mode = saved.unpaid_cancel_mode;
  return (
    <SettingCard
      testId="booking-timers-card"
      title="Unpaid bookings"
      dirty={draft.isDirty('booking_timers')}
      refusal={refusal('booking_timers')}
      chip={
        <StatusChip
          testId="status-unpaid_bookings"
          tone={modeTone(mode)}
          since={mode === 'off' ? null : latest?.unpaid_bookings?.state_since}
        >
          {mode === 'count' ? 'Auto-cancel: Count only' : `Auto-cancel ${MODE_CHIP[mode]}`}
        </StatusChip>
      }
      description={
        <p>
          A booking left unpaid is cancelled automatically, and the customer is told. A customer who
          is paying right now is never cancelled. The payment hold keeps the car for a customer who
          has started paying; their payment link stops working when it ends. Changes apply from the
          next payment or run.
        </p>
      }
      footer={<LastChanged setting="unpaid_bookings" latest={latest} />}
    >
      <ModeControl
        label="Auto-cancel"
        name="unpaid_cancel_mode"
        value={timers.unpaid_cancel_mode}
        help={UNPAID_HELP}
        onChange={(m) => draft.set('booking_timers', { ...timers, unpaid_cancel_mode: m })}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label="Cancel after (minutes unpaid, 5–1440)" name="unpaid_cancel_minutes">
          <Input
            id="unpaid_cancel_minutes"
            type="number"
            min={5}
            max={1440}
            step="1"
            value={timers.unpaid_cancel_minutes}
            onChange={(e) =>
              draft.set('booking_timers', {
                ...timers,
                unpaid_cancel_minutes: Number(e.target.value),
              })
            }
          />
        </FormField>
        <FormField label="Payment hold (minutes, 5–60)" name="booking_hold_minutes">
          <Input
            id="booking_hold_minutes"
            type="number"
            min={5}
            max={60}
            step="1"
            value={timers.hold_minutes}
            onChange={(e) =>
              draft.set('booking_timers', { ...timers, hold_minutes: Number(e.target.value) })
            }
          />
        </FormField>
      </div>
      <PendingChanges setting="unpaid_bookings" changes={draft.changesOf('booking_timers')} />
    </SettingCard>
  );
}

/** 338 (#938) + 356: the automatic booking workflow. */
export function AutoWorkflowCard({ draft, config, latest, refusal }: CardProps) {
  const saved = draft.saved?.auto_workflow;
  const current = draft.value('auto_workflow');
  if (!saved || !current) return null;
  const activatedAt = config.auto_workflow?.activated_at ?? null;
  return (
    <SettingCard
      testId="auto-workflow-card"
      title="Automatic booking workflow"
      dirty={draft.isDirty('auto_workflow')}
      refusal={refusal('auto_workflow')}
      chip={
        <StatusChip
          testId="status-auto_workflow"
          tone={modeTone(saved.mode)}
          since={saved.mode === 'off' ? null : (latest?.auto_workflow?.state_since ?? activatedAt)}
        >
          {MODE_CHIP[saved.mode]}
        </StatusChip>
      }
      description={
        <p>
          Moves paid bookings through check-out and completion by the clock, so staff do not have to
          press the buttons. Staff can still move a booking themselves. Changes apply from the next
          run (every 5 minutes).
        </p>
      }
      footer={<LastChanged setting="auto_workflow" latest={latest} />}
    >
      <ModeControl
        label="Automatic booking workflow"
        name="auto_workflow_mode"
        value={current.mode}
        help={WORKFLOW_HELP}
        onChange={(m) => draft.set('auto_workflow', { mode: m })}
      />
      <p className="text-sm text-steel">
        {activatedAt
          ? `Covers rentals ending from ${new Date(activatedAt).toLocaleString('en-MY', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              timeZone: 'Asia/Kuala_Lumpur',
            })} (Malaysia time), when it was first switched on. Older rentals are closed by a one-off clean-up.`
          : 'Not switched on yet. The first switch to Count only or On sets the date it covers rentals from.'}
      </p>
      <PendingChanges setting="auto_workflow" changes={draft.changesOf('auto_workflow')} />
    </SettingCard>
  );
}
